/**
 * ========================================
 * APP ROUTER
 * ========================================
 * Main routing configuration for the application
 * Handles authentication routes and protected app routes
 */

import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Landing } from '@components/pages/Landing';
import { MainLayout } from '@components/layouts';
import { ProtectedRoute } from './ProtectedRoute';
import { LiveClassWSProvider } from '@/context/LiveClassWSContext';
import { EntitlementProvider } from '@context/EntitlementContext';
import { LearningTimeTracker } from './LearningTimeTracker';


// ── Pages are loaded on demand ─────────────────────────────────────────────────────────────────────────────────
// Each page below is its own chunk, fetched the first time its route is opened. A visitor on the landing page (or a
// search-engine crawler) no longer downloads the whole application — live classes, the course builder, admin
// screens, charts — just to see the first screen. Landing itself stays in the main bundle.
const AskQuestion = lazy(() => import('@components/pages/AskQuestion').then(m => ({ default: m.AskQuestion })));
const Calendar = lazy(() => import('@components/pages/Calendar'));
const CalendarManagement = lazy(() => import('@components/pages/CalendarManagement'));
const CapsuleLearningPage = lazy(() => import('@components/pages/CapsuleLearningPage'));
const Chat = lazy(() => import('@components/pages/Chat').then(m => ({ default: m.Chat })));
const CouponManagement = lazy(() => import('@components/pages/admin/CouponManagement').then(m => ({ default: m.CouponManagement })));
const CourseBuilderChat = lazy(() => import('@components/pages/CourseBuilderChat').then(m => ({ default: m.CourseBuilderChat })));
const CourseCreator = lazy(() => import('@components/pages/CourseCreator').then(m => ({ default: m.CourseCreator })));
const CourseManagement = lazy(() => import('@components/pages/CourseManagement').then(m => ({ default: m.CourseManagement })));
const CoursesPage = lazy(() => import('@components/pages/CoursesPage').then(m => ({ default: m.CoursesPage })));
const Dashboard = lazy(() => import('@components/pages/Dashboard').then(m => ({ default: m.Dashboard })));
const DemoRequestManagement = lazy(() => import('@components/pages/admin/DemoRequestManagement').then(m => ({ default: m.DemoRequestManagement })));
const DoubtSessionForm = lazy(() => import('@components/pages/DoubtSessionForm').then(m => ({ default: m.DoubtSessionForm })));
const DoubtSessionManagement = lazy(() => import('@components/pages/DoubtSessionManagement').then(m => ({ default: m.DoubtSessionManagement })));
const FeatureManagement = lazy(() => import('@components/pages/admin/FeatureManagement').then(m => ({ default: m.FeatureManagement })));
const GettingStarted = lazy(() => import('@components/pages/GettingStarted'));
const HandwritingPractice = lazy(() => import('@components/pages/HandwritingPractice'));
const InstructorCategoryMatrix = lazy(() => import('@components/pages/LanguageLearning/InstructorCategoryMatrix'));
const InstructorContentStructure = lazy(() => import('@components/pages/LanguageLearning/InstructorContentStructure'));
const JoinClass = lazy(() => import('@components/pages/JoinClass').then(m => ({ default: m.JoinClass })));
const JoinSchool = lazy(() => import('@components/pages/JoinSchool'));
const KnowledgeCapsuleManagement = lazy(() => import('@components/pages/KnowledgeCapsuleManagement'));
const Lab = lazy(() => import('@components/pages/Lab').then(m => ({ default: m.Lab })));
const LaTeXTester = lazy(() => import('@components/pages/LaTeXTester').then(m => ({ default: m.LaTeXTester })));
const LeadManagement = lazy(() => import('@components/pages/admin/LeadManagement').then(m => ({ default: m.LeadManagement })));
const LearnerActivity = lazy(() => import('@components/pages/LanguageLearning/LearnerActivity'));
const LearnerHome = lazy(() => import('@components/pages/LanguageLearning/LearnerHome'));
const LearnerProgress = lazy(() => import('@components/pages/LanguageLearning/LearnerProgress'));
const LessonCurriculumEditor = lazy(() => import('@components/pages/LanguageLearning/LessonCurriculumEditor'));
const LessonDetails = lazy(() => import('@components/pages/LanguageLearning/LessonDetails'));
const LiveClass = lazy(() => import('@components/pages/LiveClass').then(m => ({ default: m.LiveClass })));
const LiveVoice = lazy(() => import('@components/pages/LiveVoice').then(m => ({ default: m.LiveVoice })));
const LoginPage = lazy(() => import('@components/pages/LoginPage').then(m => ({ default: m.LoginPage })));
const MentorActionItemManagement = lazy(() => import('@components/pages/admin/MentorActionItemManagement').then(m => ({ default: m.MentorActionItemManagement })));
const MentorAssignmentManagement = lazy(() => import('@components/pages/admin/MentorAssignmentManagement').then(m => ({ default: m.MentorAssignmentManagement })));
const MentorDashboard = lazy(() => import('@components/pages/MentorDashboard').then(m => ({ default: m.MentorDashboard })));
const MilestoneProgress = lazy(() => import('@components/pages/LanguageLearning/MilestoneProgress'));
const MyClasses = lazy(() => import('@components/pages/MyClasses'));
const MyInstructor = lazy(() => import('@components/pages/MyInstructor').then(m => ({ default: m.MyInstructor })));
const MyLearnings = lazy(() => import('@components/pages/MyLearnings').then(m => ({ default: m.MyLearnings })));
const MyProfile = lazy(() => import('@components/pages/MyProfile').then(m => ({ default: m.MyProfile })));
const MyTeaching = lazy(() => import('@components/pages/MyTeaching').then(m => ({ default: m.MyTeaching })));
const NotificationsAndJobs = lazy(() => import('@components/pages/NotificationsAndJobs').then(m => ({ default: m.NotificationsAndJobs })));
const ParentActivity = lazy(() => import('@components/pages/parent/ParentPages').then(m => ({ default: m.ParentActivity })));
const ParentAssessments = lazy(() => import('@components/pages/parent/ParentPages').then(m => ({ default: m.ParentAssessments })));
const ParentHome = lazy(() => import('@components/pages/parent/ParentPages').then(m => ({ default: m.ParentHome })));
const ParentInsights = lazy(() => import('@components/pages/parent/ParentPages').then(m => ({ default: m.ParentInsights })));
const ParentPayments = lazy(() => import('@components/pages/parent/ParentPages').then(m => ({ default: m.ParentPayments })));
const ParentProgress = lazy(() => import('@components/pages/parent/ParentPages').then(m => ({ default: m.ParentProgress })));
const ParentRegisterPage = lazy(() => import('@components/pages/ParentRegisterPage'));
const PaymentError = lazy(() => import('@components/payment/PaymentError').then(m => ({ default: m.PaymentError })));
const PaymentHistory = lazy(() => import('@components/pages/PaymentHistory').then(m => ({ default: m.PaymentHistory })));
const PaymentSuccess = lazy(() => import('@components/payment/PaymentSuccess').then(m => ({ default: m.PaymentSuccess })));
const PlanManagement = lazy(() => import('@components/pages/admin/PlanManagement').then(m => ({ default: m.PlanManagement })));
const Pricing = lazy(() => import('@components/pages/Pricing').then(m => ({ default: m.Pricing })));
const PromptsManagement = lazy(() => import('@components/pages/PromptsManagement'));
const ReadingAssessment = lazy(() => import('@components/pages/ReadingAssessment').then(m => ({ default: m.ReadingAssessment })));
const ReadingReports = lazy(() => import('@components/pages/ReadingReports').then(m => ({ default: m.ReadingReports })));
const RegisterPage = lazy(() => import('@components/pages/RegisterPage').then(m => ({ default: m.RegisterPage })));
const ReplayAcademy = lazy(() => import('@components/pages/ReplayAcademy').then(m => ({ default: m.ReplayAcademy })));
const ReplayAcademyManagement = lazy(() => import('@components/pages/ReplayAcademyManagement'));
const RevenueDashboard = lazy(() => import('@components/pages/admin/RevenueDashboard').then(m => ({ default: m.RevenueDashboard })));
const RolePermissionManagement = lazy(() => import('@components/pages/RolePermissionManagement').then(m => ({ default: m.RolePermissionManagement })));
const SchemaGenerationTest = lazy(() => import('@components/pages/SchemaGenerationTest').then(m => ({ default: m.SchemaGenerationTest })));
const SchoolConsole = lazy(() => import('@components/pages/SchoolConsole'));
const SchoolEnroll = lazy(() => import('@components/pages/SchoolEnroll'));
const SchoolManagement = lazy(() => import('@components/pages/SchoolManagement'));
const Settings = lazy(() => import('@components/pages/Settings').then(m => ({ default: m.Settings })));
const SocialMediaManagement = lazy(() => import('@components/pages/SocialMediaManagement'));
const VideoStudioList = lazy(() => import('@components/pages/VideoStudio/VideoStudioList'));
const VideoStudioEditor = lazy(() => import('@components/pages/VideoStudio/VideoStudioEditor'));
const YouTubeGrowth = lazy(() => import('@components/pages/YouTubeGrowth'));
const StudyMaterial = lazy(() => import('@components/pages/StudyMaterial'));
const LessonPlanPage = lazy(() => import('@components/pages/LessonPlanPage'));
const SubscriptionManagement = lazy(() => import('@components/pages/SubscriptionManagement').then(m => ({ default: m.SubscriptionManagement })));
const SupportDashboard = lazy(() => import('@components/pages/SupportDashboard').then(m => ({ default: m.SupportDashboard })));
const TestManagementPage = lazy(() => import('@components/pages/TestManagementPage'));
const TicketDetail = lazy(() => import('@components/pages/TicketDetail').then(m => ({ default: m.TicketDetail })));
const TicketManagement = lazy(() => import('@components/pages/TicketManagement').then(m => ({ default: m.TicketManagement })));
const TodoList = lazy(() => import('@components/pages/TodoList').then(m => ({ default: m.TodoList })));
const TopicRecorder = lazy(() => import('@components/pages/TopicRecorder').then(m => ({ default: m.TopicRecorder })));
const UserManagement = lazy(() => import('@components/pages/UserManagement').then(m => ({ default: m.UserManagement })));
const WhatsAppExecutionEngine = lazy(() => import('@components/pages/WhatsAppExecutionEngine'));

/** Shown for the moment a page's code is being fetched. */
const RouteLoading: React.FC = () => (
  <div role="status" aria-label="Loading" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
    <div style={{ width: 36, height: 36, border: '3px solid rgba(99,102,241,0.25)', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
  </div>
);

interface AppRouterProps {
  appContent: React.ReactNode;
}

/**
 * AppRouter Component
 * Sets up all routes for the application
 */
export const AppRouter: React.FC<AppRouterProps> = ({ appContent }) => {
  return (
    <Router>
      <EntitlementProvider>
      {/* Silent learning-time heartbeat for learners (renders nothing) */}
      <LearningTimeTracker />
      <Suspense fallback={<RouteLoading />}>
      <Routes>
        {/* Landing Page Route */}
        <Route path="/" element={<Landing />} />

        {/* Pricing Page Route */}
        <Route path="/pricing" element={<Pricing />} />

        {/* Authentication Routes */}
        <Route path="/auth/login" element={<LoginPage />} />
        <Route path="/auth/register" element={<RegisterPage />} />

        {/* Parent accounts: sign-up is public; the dashboard is a read-only layer over a linked child's learning */}
        <Route path="/auth/register-parent" element={<ParentRegisterPage />} />
        <Route
          path="/parent"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ParentHome />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/progress"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ParentProgress />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/assessments"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ParentAssessments />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/activity"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ParentActivity />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/insights"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ParentInsights />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent/payments"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ParentPayments />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* School self-enrollment — a school's standing, shareable link; unauthenticated,
            no sidebar (the student has no account yet). */}
        <Route path="/enroll-school/:token" element={<SchoolEnroll />} />

        {/* School invite — public (not ProtectedRoute): a brand-new visitor with no account yet
            must be able to reach this page to create one and join. JoinSchool itself branches
            on isAuthenticated for the "already have an account" vs. "create one inline" case. */}
        <Route path="/join-school/:token" element={<JoinSchool />} />

        {/* Getting Started -- required first-run step (class / board / type -> auto-assigned courses). Standalone, no sidebar. */}
        <Route
          path="/getting-started"
          element={
            <ProtectedRoute>
              <GettingStarted />
            </ProtectedRoute>
          }
        />

      {/* Courses Route */}
        <Route
          path="/courses"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CoursesPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Conversational course builder */}
        <Route
          path="/courses/design-with-ai"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CourseBuilderChat />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/courses/design-with-ai/:sessionId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CourseBuilderChat />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Create Course Route */}
        <Route
          path="/create-course"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CourseCreator />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Course Management Route - Create new course */}
        <Route
          path="/course-management"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CourseManagement />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Course Management Route - Edit existing course */}
        <Route
          path="/course-management/:courseId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CourseManagement />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Lab Route */}
        <Route
          path="/lab"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Lab />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* User Management Route */}
        <Route
          path="/users"
          element={
            <ProtectedRoute>
              <MainLayout>
                <UserManagement />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Role & Permission Management Route */}
        <Route
          path="/roles-permissions"
          element={
            <ProtectedRoute>
              <MainLayout>
                <RolePermissionManagement />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Dashboard Route */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Dashboard />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Chat Route */}
        <Route
          path="/chat"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Chat />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Voice Route - Schema based */}
        <Route
          path="/voice"
          element={
            <ProtectedRoute>
              {appContent}
            </ProtectedRoute>
          }
        />

        {/* My Learnings Route */}
        <Route
          path="/my-learnings"
          element={
            <ProtectedRoute>
              <MainLayout>
                <MyLearnings />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Virtual Tutor Route */}
        <Route
          path="/my-instructor"
          element={
            <ProtectedRoute>
              <MainLayout>
                <MyInstructor />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Reading Assessment Route */}
        <Route
          path="/reading-assessment/:topicId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ReadingAssessment />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Reading Reports — cross-topic score history/metrics, reached from a Reading Assessment result */}
        <Route
          path="/reading-reports"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ReadingReports />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Lesson Plan Route — the topic's lesson plan as a page (📚 button on the lesson page) */}
        <Route
          path="/lesson-plan/:courseId/:topicId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <LessonPlanPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Study Material Route — generic per-topic content viewer (video/audio/pdf/image/text) */}
        <Route
          path="/study-material/:courseId/:topicId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <StudyMaterial />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Handwriting Practice Route — webcam/upload photo of handwriting -> legibility + accuracy feedback */}
        <Route
          path="/handwriting-practice/:courseId/:topicId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <HandwritingPractice />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Replay Academy Route */}
        <Route
          path="/replay-academy"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ReplayAcademy />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Calendar Route */}
        <Route
          path="/calendar"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Calendar />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Doubt Session Routes */}
        <Route
          path="/doubt-session"
          element={
            <ProtectedRoute>
              <MainLayout>
                <DoubtSessionManagement />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/doubt-session/new"
          element={
            <ProtectedRoute>
              <MainLayout>
                <DoubtSessionForm />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/doubt-session/:id/edit"
          element={
            <ProtectedRoute>
              <MainLayout>
                <DoubtSessionForm />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Ticket Management Routes */}
        <Route
          path="/tickets"
          element={
            <ProtectedRoute>
              <MainLayout>
                <TicketManagement />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/tickets/dashboard"
          element={
            <ProtectedRoute>
              <MainLayout>
                <SupportDashboard />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Knowledge Capsule Management Route */}
        <Route
          path="/knowledge-capsules"
          element={
            <ProtectedRoute>
              <MainLayout>
                <KnowledgeCapsuleManagement />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Knowledge Capsule Learning Page -- blog-style single page view */}
        <Route
          path="/knowledge-capsules/:id/learn"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CapsuleLearningPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Language Learning -- learner screens */}
        <Route
          path="/language-learning"
          element={
            <ProtectedRoute>
              <MainLayout>
                <LearnerHome />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/language-learning/lessons/:lessonId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <LessonDetails />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/language-learning/lessons/:lessonId/progress"
          element={
            <ProtectedRoute>
              <MainLayout>
                <MilestoneProgress />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/language-learning/activity/:sessionId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <LearnerActivity />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/language-learning/progress"
          element={
            <ProtectedRoute>
              <MainLayout>
                <LearnerProgress />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Language Learning -- instructor screens */}
        <Route
          path="/language-learning/instructor/structure"
          element={
            <ProtectedRoute>
              <MainLayout>
                <InstructorContentStructure />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/language-learning/instructor/lessons/:lessonId/curriculum"
          element={
            <ProtectedRoute>
              <MainLayout>
                <LessonCurriculumEditor />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/language-learning/instructor/matrix"
          element={
            <ProtectedRoute>
              <MainLayout>
                <InstructorCategoryMatrix />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/notifications"
          element={
            <ProtectedRoute>
              <MainLayout>
                <NotificationsAndJobs />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Social Media Advertising (Instagram Reels / YouTube Shorts) -- Admin only, enforced server-side */}
        <Route
          path="/admin/social-media"
          element={
            <ProtectedRoute>
              <MainLayout>
                <SocialMediaManagement />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* AI Video Studio -- script -> scenes -> timeline editor -> rendered video. Admin only, enforced server-side */}
        <Route
          path="/admin/video-studio"
          element={
            <ProtectedRoute>
              <MainLayout>
                <VideoStudioList />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/video-studio/:projectId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <VideoStudioEditor />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/youtube-growth"
          element={
            <ProtectedRoute>
              <MainLayout>
                <YouTubeGrowth />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* WhatsApp Execution Engine -- launch + monitor a broadcast from a flow defined on the page above */}
        <Route
          path="/admin/whatsapp-broadcasts"
          element={
            <ProtectedRoute>
              <MainLayout>
                <WhatsAppExecutionEngine />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Subscription / Billing Routes */}
        <Route
          path="/subscription"
          element={
            <ProtectedRoute>
              <MainLayout>
                <SubscriptionManagement />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payment-history"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PaymentHistory />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/error" element={<PaymentError />} />

        {/* Payment Admin Routes */}
        <Route
          path="/admin/features"
          element={
            <ProtectedRoute>
              <MainLayout>
                <FeatureManagement />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/plans"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PlanManagement />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/coupons"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CouponManagement />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/revenue"
          element={
            <ProtectedRoute>
              <MainLayout>
                <RevenueDashboard />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Mentor Program Routes */}
        <Route
          path="/admin/mentor-action-items"
          element={
            <ProtectedRoute>
              <MainLayout>
                <MentorActionItemManagement />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/mentor-assignments"
          element={
            <ProtectedRoute>
              <MainLayout>
                <MentorAssignmentManagement />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/demo-requests"
          element={
            <ProtectedRoute>
              <MainLayout>
                <DemoRequestManagement />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/leads"
          element={
            <ProtectedRoute>
              <MainLayout>
                <LeadManagement />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/mentor-dashboard"
          element={
            <ProtectedRoute>
              <MainLayout>
                <MentorDashboard />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/tickets/:id"
          element={
            <ProtectedRoute>
              <MainLayout>
                <TicketDetail />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Ask Question Route */}
        <Route
          path="/ask-question"
          element={
            <ProtectedRoute>
              <MainLayout>
                <AskQuestion />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Doubt Session Route */}
        <Route
          path="/live-voice"
          element={
            <ProtectedRoute>
              <MainLayout userName="John Doe" userEmail="john@example.com" userAvatar="👤">
                <LiveVoice />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* LaTeX Tester Route */}
        <Route
          path="/latex-tester"
          element={
            <ProtectedRoute>
              <MainLayout>
                <LaTeXTester />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Settings Route */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Settings />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* My Profile Route */}
        <Route
          path="/my-profile"
          element={
            <ProtectedRoute>
              <MainLayout>
                <MyProfile />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Prompts Management Route */}
        <Route
          path="/prompts"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PromptsManagement />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Test Management Route */}
        <Route
          path="/test-management"
          element={
            <ProtectedRoute>
              <MainLayout>
                <TestManagementPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Replay Academy Management Route */}
        <Route
          path="/replay-academy-management"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ReplayAcademyManagement />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Calendar Management Route */}
        <Route
          path="/calendar-management"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CalendarManagement />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Todo List Route */}
        <Route
          path="/todos"
          element={
            <ProtectedRoute>
              <MainLayout>
                <TodoList />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Schema Generation Test Route */}
        <Route
          path="/schema-test"
          element={
            <ProtectedRoute>
              <MainLayout>
                <SchemaGenerationTest />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* My Teaching Route */}
        <Route
          path="/my-teaching"
          element={
            <ProtectedRoute>
              <MainLayout>
                <MyTeaching />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* My Classes (Recordings) — teacher delivered / student attended */}
        <Route
          path="/my-classes"
          element={
            <ProtectedRoute>
              <MainLayout>
                <MyClasses />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Live Class — Teacher (full-screen, no MainLayout) */}
        <Route
          path="/live-class"
          element={
            <ProtectedRoute>
              <LiveClassWSProvider>
                <LiveClass />
              </LiveClassWSProvider>
            </ProtectedRoute>
          }
        />

        {/* Join Class — Student (full-screen, no MainLayout) */}
        <Route
          path="/join-class"
          element={
            <ProtectedRoute>
              <LiveClassWSProvider>
                <JoinClass />
              </LiveClassWSProvider>
            </ProtectedRoute>
          }
        />

        {/* Topic Recorder — teacher records video / voice / board image for a course topic. Full-screen, opened in a NEW TAB
            by the course editor so the editor's unsaved course state survives (see utils/topicRecorderBridge.ts). */}
        <Route
          path="/topic-recorder"
          element={
            <ProtectedRoute>
              <TopicRecorder />
            </ProtectedRoute>
          }
        />

        {/* School (B2B) — console for school admins (gated by the school_admin feature via featureRegistry),
            super-admin school management, and the invite acceptance page. Each console tab
            (Overview/Classes/Members/Invites) is its own route so it's linkable/bookmarkable and
            back/forward works between tabs, not just internal component state. /school alone
            redirects to /school/overview, preserving ?school_id= for a super admin viewing a
            specific school. */}
        <Route path="/school" element={<Navigate to={{ pathname: '/school/overview', search: window.location.search }} replace />} />
        <Route
          path="/school/:tab"
          element={
            <ProtectedRoute>
              <MainLayout>
                <SchoolConsole />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/schools"
          element={
            <ProtectedRoute>
              <MainLayout>
                <SchoolManagement />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
      </EntitlementProvider>
    </Router>
  );
};

export default AppRouter;
